import { useState, useCallback, useEffect, useMemo } from "react";
import axios from "axios";
import { NONE_PROFILE_ID, isSameProfile } from "../utils/profileUtils";
import { buildVipInfoForSidebar } from "../utils/vipUtils";

/**
 * useProfile — 档案与会员状态管理 Hook
 *
 * 管理当前选中的儿童档案（currentProfile）和会员信息（memberInfo），
 * 提供档案查询、会员状态刷新、跨页面档案自动同步等功能。
 *
 * @param {object} options
 * @param {string} options.userId - 当前用户 ID
 * @param {boolean} options.isVisitor - 是否为游客模式
 * @param {string} [options.currentPage] - 当前页面标识（用于页面切换时自动同步档案）
 * @returns {{
 *   currentProfile: object|null,      // 当前选中的儿童档案对象，null 表示未加载或无档案
 *   setCurrentProfile: Function,      // 手动设置当前档案（如用户切换档案后更新全局状态）
 *   memberInfo: object|null,          // 当前用户的会员信息（等级、到期时间等），null 表示未获取
 *   fetchDefaultProfile: Function,    // 拉取并自动设置默认档案（优先 defaultProfile 标记，其次取第一个）
 *   fetchAllProfiles: Function,       // 拉取用户的全部档案列表，不做筛选直接返回数组
 *   fetchMemberStatus: Function,      // 从后端刷新会员状态（游客模式自动跳过）
 *   sidebarVipInfo: object,           // 根据 memberInfo 计算出的侧边栏 VIP 展示信息 { level, expireTime, ... }
 * }}
 */
export const useProfile = ({ userId, isVisitor = true, currentPage }) => {
    const [currentProfile, setCurrentProfile] = useState(null);
    const [memberInfo, setMemberInfo] = useState(null);

    // --- 档案查询（获取全量列表）---
    const fetchAllProfiles = useCallback(async (uid) => {
        if (!uid) return null;

        try {
            const { data: res } = await axios.post(
                `${window.PUBLIC_URL}/medagent/robot/searchProfile`,
                { askUserCode: uid },
            );

            if (res?.code !== 200) {
                throw new Error(res?.msg || "儿童档案加载失败，请稍后再试");
            }

            return Array.isArray(res.data) ? res.data : [];
        } catch (err) {
            console.error("获取档案列表失败", err);
            throw err;
        }
    }, []);

    // --- 档案查询（获取并设置默认档案）---
    const fetchDefaultProfile = useCallback(
        async (uid) => {
            if (!uid) return null;

            try {
                const profiles = await fetchAllProfiles(uid);

                if (profiles && profiles.length > 0) {
                    const defaultPerson =
                        profiles.find((p) => p.defaultProfile) || profiles[0];
                    setCurrentProfile(defaultPerson);
                    return defaultPerson;
                }
            } catch (err) {
                // fetchAllProfiles 内已打印日志
            }

            return null;
        },
        [fetchAllProfiles],
    );

    // --- 会员状态查询 ---
    const fetchMemberStatus = useCallback(async () => {
        if (!userId || isVisitor) {
            setMemberInfo(null);
            return null;
        }

        try {
            const { data: res } = await axios.post(
                `${window.PUBLIC_URL}/medagent/member/status`,
                { askUserCode: userId },
            );

            if (res?.code == 200) {
                const nextInfo = res.data || null;
                setMemberInfo(nextInfo);
                return nextInfo;
            }

            setMemberInfo(null);
            return null;
        } catch (err) {
            console.error("查询会员状态失败", err);
            setMemberInfo(null);
            return null;
        }
    }, [userId, isVisitor]);

    // --- 页面挂载时查询会员状态 ---
    useEffect(() => {
        fetchMemberStatus();
    }, [fetchMemberStatus]);

    // --- 页面切换时同步档案（检测档案是否被其他页面修改）---
    useEffect(() => {
        const syncGlobalProfileWithServer = async () => {
            const uid = localStorage.getItem("medicalUserId") || userId;
            if (!uid || !currentProfile || currentProfile.id === NONE_PROFILE_ID)
                return;

            try {
                const profiles = await fetchAllProfiles(uid);

                if (profiles && profiles.length > 0) {
                    const freshPerson = profiles.find(
                        (p) => isSameProfile(p, currentProfile),
                    );

                    fetchMemberStatus();

                    if (
                        freshPerson &&
                        JSON.stringify(freshPerson) !== JSON.stringify(currentProfile)
                    ) {
                        console.log(
                            ">>> [全局状态自动同步] 检测到档案已被修改，已无感刷新上下文:",
                            freshPerson.profileNick,
                        );
                        setCurrentProfile(freshPerson);
                    }
                }
            } catch (e) {
                console.error(">>> [全局状态自动同步] 异常失败:", e);
            }
        };

        syncGlobalProfileWithServer();
    }, [currentPage, fetchAllProfiles, fetchMemberStatus, currentProfile, userId]);

    // --- 侧边栏会员展示信息 ---
    const sidebarVipInfo = useMemo(
        () => buildVipInfoForSidebar(memberInfo),
        [memberInfo],
    );

    return {
        currentProfile,
        setCurrentProfile,
        memberInfo,
        fetchDefaultProfile,
        fetchAllProfiles,
        fetchMemberStatus,
        sidebarVipInfo,
    };
};
